import { uploadDsd } from "@/apis/dsd-dsd-compare/route";
import { logUserAction } from "@/apis/logs/route";
import {
  PeriodChip,
  toReportPeriod,
  toReportType,
  TypeChip,
  YearChip,
} from "@/components/common/report-chip";
import { useDsdCompareHistory } from "@/hooks/queries/use-dsd-compare-history";
import { Avatar } from "@/toggle-ui-kit/avatar";
import { Button } from "@/toggle-ui-kit/button";
import { openInNewTab } from "@/utils/open-in-new-tab";
import {
  openPendingTab,
  resolvePendingTab,
  warnPopupBlocked,
} from "@/utils/pending-tab";
import { getFormattedDateCompact, parseServerUtcDate } from "@/utils/time";
import { logHomeServiceAccess, logHomeWork } from "@/utils/usage-log";
import { useMutation } from "@tanstack/react-query";
import { ChevronRight, GitCompare, History, Loader2 } from "lucide-react";
import { Fragment, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { FileDropArea } from "@/components/file-drop-zone";
import { acceptErrorMessage } from "@/utils/file-accept";
import { fillFileSlots, overflowError, type FileSlots } from "./file-slots";
import { FileSlotPair } from "./file-slot-pair";
import { Notice } from "./notice";

const HISTORY_LIMIT = 4;

// 집계에서 STEP 카드 로그와 묶이도록 steps.tsx 의 label 과 같게 쓴다
const STEP_LABEL = "DSD 대사";

const compareResultUrl = (srcId: string, trgId: string) =>
  `/compare/dsd-dsd/result?src=${srcId}&trg=${trgId}`;

const formatTime = (iso: string) =>
  parseServerUtcDate(iso).toLocaleTimeString("ko-KR", {
    timeZone: "Asia/Seoul",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

/**
 * 비교 내역이 없을 때. 대사 화면 사이드바에도 같은 모양이 있지만 그 화면의 내부 구현을
 * 가져오면 화면 간 import 가 된다 — 세 번째 사용처가 생기면 공용으로 뺀다.
 */
const EmptyHistory = () => (
  <div className="flex flex-1 flex-col items-center justify-center gap-2">
    <div className="flex items-center rounded-lg bg-neutral-100 p-3">
      <History size={20} className="text-neutral-500" />
    </div>
    <p className="body-2-normal-medium text-label-normal">
      비교 내역이 아직 없습니다.
    </p>
  </div>
);

const CompareHistory = () => {
  const navigate = useNavigate();
  // 첫 페이지만 쓴다 — 전체 내역은 더보기(대사 화면)에서 본다.
  // 내역은 결과 탭의 compare 호출이 만들므로 홈에서는 무효화할 시점이 없다.
  // staleTime 0 으로 두면 결과 탭을 보고 홈으로 돌아올 때(창 포커스) 다시 받는다.
  const { items, isLoading } = useDsdCompareHistory(HISTORY_LIMIT, {
    staleTime: 0,
  });

  return (
    <div className="flex w-full flex-col gap-2 pt-6">
      <div className="flex items-center justify-between">
        <p className="label-1-normal-semibold text-label-neutral">비교 내역</p>
        <Button
          size={28}
          className="border-0"
          rightIcon={<ChevronRight strokeWidth={1.8} />}
          onClick={() => {
            logHomeServiceAccess(`${STEP_LABEL} 패널`, "비교 내역 더보기");
            navigate("/compare/dsd-dsd");
          }}
        >
          더보기
        </Button>
      </div>
      {!isLoading && items.length === 0 && (
        <div className="flex rounded-lg border border-line-normal py-10">
          <EmptyHistory />
        </div>
      )}
      {items.length > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-line-normal p-4">
          {items.map((item, i) => {
            const reportType = toReportType(item.reportType);
            const period = toReportPeriod(item.period);

            return (
              <Fragment key={item.id}>
                {i > 0 && <div className="h-px w-full bg-line-normal" />}
                <button
                  type="button"
                  className="-m-2 flex flex-col gap-2 rounded-md p-2 text-left transition-colors hover:bg-neutral-50"
                  onClick={() => {
                    logUserAction("DSD 비교내역 다시 열기");
                    logHomeServiceAccess(
                      `${STEP_LABEL} 패널`,
                      "비교 내역 다시 열기",
                    );
                    openInNewTab(
                      compareResultUrl(
                        item.sourceFile.fileId,
                        item.targetFile.fileId,
                      ),
                    );
                  }}
                >
                  <div className="flex w-full items-center gap-1">
                    {/* 모르는 값은 칩을 빼 추측값을 단언하지 않는다 */}
                    {reportType && <TypeChip type={reportType} size="small" />}
                    {item.year && <YearChip year={item.year} size="small" />}
                    {period && <PeriodChip period={period} size="small" />}
                    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
                      <p className="caption-1-medium shrink-0 text-label-alternative">
                        {getFormattedDateCompact(item.requestedAt)}{" "}
                        {formatTime(item.requestedAt)}
                      </p>
                      {item.uploaderName && (
                        <div className="flex min-w-0 items-center gap-1">
                          <Avatar value={item.uploaderName} size="16" />
                          <p className="caption-1-medium truncate text-label-alternative">
                            {item.uploaderName}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex w-full flex-col gap-1">
                    <p className="label-2-regular truncate text-label-normal">
                      {item.sourceFile.filename}
                    </p>
                    <p className="label-2-regular truncate text-label-normal">
                      {item.targetFile.filename}
                    </p>
                  </div>
                </button>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DSD_ACCEPT = ".dsd";

/** STEP3 DSD 대사 — DSD 두 개를 올려 대사 결과를 새 탭으로 열고, 최근 비교 내역을 보여준다. */
export const DsdComparePanel = () => {
  const [[prev, latest], setFiles] = useState<FileSlots>([null, null]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // 업로드 응답을 기다린 뒤 열면 팝업 차단에 걸리므로 클릭 시점에 연 탭의 주소를 나중에 바꾼다
  const pendingTab = useRef<Window | null>(null);

  const { mutate, isPending } = useMutation({
    // 한쪽만 실패해도 어느 파일이 문제인지 이름으로 알려준다. 성공한 쪽은 서버에 남아
    // 다시 시도하면 백엔드가 "이름 (1).dsd" 로 새로 쌓는다(대사 결과에는 영향 없음).
    mutationFn: async ([prevFile, latestFile]: [File, File]) => {
      const attempt = (file: File) =>
        uploadDsd(file).then(
          (uploaded) => ({ file, uploaded }),
          () => ({ file, uploaded: undefined }),
        );
      const [src, trg] = await Promise.all([
        attempt(prevFile),
        attempt(latestFile),
      ]);
      if (!src.uploaded || !trg.uploaded) {
        const failed = [src, trg].filter((r) => !r.uploaded);
        throw new Error(failed.map((r) => r.file.name).join(", "));
      }
      return [src.uploaded, trg.uploaded] as const;
    },
    onSuccess: ([src, trg]) => {
      logUserAction("DSD 보고서 대사", [
        { filename: src.filename, dsdFileId: src.fileId },
        { filename: trg.filename, dsdFileId: trg.fileId },
      ]);
      const url = compareResultUrl(src.fileId, trg.fileId);
      const tab = pendingTab.current;
      pendingTab.current = null;
      // 업로드는 성공했다 — 탭을 못 열면 같은 탭 이동이라도 줘야 조용히 묻히지 않는다
      if (!resolvePendingTab(tab, url)) {
        warnPopupBlocked("대사 결과 열기", () => navigate(url));
      }
    },
    onError: (error) => {
      pendingTab.current?.close();
      pendingTab.current = null;
      toast.error(
        `${error.message} 업로드에 실패했습니다. 올바른 DSD 파일인지 확인해주세요.`,
      );
    },
  });

  return (
    <>
      <div className="flex w-full flex-col items-end gap-4">
        <Notice>대사를 위해 DSD파일 2개를 업로드해주세요</Notice>
        <FileDropArea
          accept={DSD_ACCEPT}
          hint="*.dsd 형식"
          maxFiles={2}
          selectedHint={
            prev && latest
              ? "다른 파일로 교체하려면 클릭하거나 드래그하세요"
              : undefined
          }
          error={error}
          onReject={({ message }) => setError(message)}
          onFilesSelect={(files, { rejected }) => {
            // 남은 칸보다 많이 고르면 뒤엣것이 앞엣것을 덮는다 — 칸을 바꾸지 않고 인라인으로 알린다
            const invalid = overflowError([prev, latest], files);
            setError(
              invalid ?? (rejected > 0 ? acceptErrorMessage(DSD_ACCEPT) : null),
            );
            if (invalid) return;
            setFiles((slots) => fillFileSlots(slots, files));
          }}
        >
          {(prev || latest) && (
            <FileSlotPair
              slots={[
                {
                  label: "이전 파일",
                  format: "dsd",
                  file: prev,
                  onRemove: () => {
                    setFiles(([, l]) => [null, l]);
                    setError(null);
                  },
                },
                {
                  label: "최신 파일",
                  format: "dsd",
                  file: latest,
                  onRemove: () => {
                    setFiles(([p]) => [p, null]);
                    setError(null);
                  },
                },
              ]}
              onSwap={() => setFiles(([p, l]) => [l, p])}
            />
          )}
        </FileDropArea>
      </div>
      <div className="flex w-full items-start justify-between">
        <Button
          size={40}
          onClick={() => {
            logHomeServiceAccess(`${STEP_LABEL} 패널`, "이전 DSD 불러오기");
            navigate("/compare/dsd-dsd");
          }}
        >
          이전 DSD 불러오기
        </Button>
        <Button
          variant="solid"
          size={40}
          className="bg-blue-500"
          leftIcon={
            isPending ? (
              <Loader2 className="animate-spin" strokeWidth={1.8} />
            ) : (
              <GitCompare strokeWidth={1.8} />
            )
          }
          disabled={!prev || !latest || isPending}
          onClick={() => {
            if (!prev || !latest) return;
            logHomeWork(STEP_LABEL, "대사 시작");
            pendingTab.current = openPendingTab({
              title: "DSD 대사 준비 중...",
              message: "DSD 파일을 올리고 있습니다...",
            });
            mutate([prev, latest]);
          }}
        >
          대사 시작
        </Button>
      </div>
      <CompareHistory />
    </>
  );
};
